import styled, { css, keyframes } from "styled-components";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const animateNext = keyframes`
  0% {
    transform: translateX(10px);
  }

  to {
    transform: translateX(0);
  }
`;

const animatePrev = keyframes`
  0% {
    transform: translateX(-10px);
  }

  to {
    transform: translateX(0);
  }
`;

export const CalendarHeader = styled("div")(({ theme, centered }) => ({
  ...theme.mixins.flexCenterBetween,
  ...(centered ? { justifyContent: "center" } : {}),
  borderBottom: `1px solid ${theme.palette.fly.hairlineLight}`,
  padding: 10,
}));

export const CalendarHeaderText = styled("span")(({ theme }) => ({
  fontSize: 14,
  color: "#333",
  lineHeight: "16px",
}));

export const CalendarHeaderButton = styled("button")(({ theme }) => ({
  width: "100%",
  height: 28,
  border: "none",
  backgroundColor: theme.palette.fly.inputBg,
  color: theme.palette.fly.brand,
  padding: 0,
  margin: 0,
  maxWidth: 185,
  fontWeight: 600,
  cursor: "pointer",
  borderRadius: 7,
  fontSize: 12,
  transition: "all .2s ease",

  "&:hover": {
    backgroundColor: theme.palette.fly.brand,
    color: theme.palette.white,
  },
}));

export const CalendarBody = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
}));

export const CalendarMonthsNamesBlock = styled("div")(({ theme }) => ({
  padding: 10,
  backgroundColor: theme.palette.fly.inputBg,
  width: 88,
  borderRadius: "0 0 0 8px",
}));

export const CalendarMonthsNamesList = styled("div")(({ theme }) => ({
  ...theme.mixins.flexColumn,
  gap: 9,
  flexGrow: 1,
}));

export const CalendarMonthName = styled("button")(({ theme, type }) => ({
  border: "none",
  background: "transparent",
  padding: 0,
  margin: 0,
  fontSize: 13,
  textAlign: "left",
  lineHeight: "16px",
  cursor: "pointer",

  "&.active": {
    fontWeight: 600,
    color: "#2563EB",
  },

  "&.year": {
    cursor: "default",
  },
}));

export const CalendarMonthsBlock = styled("div")(({ theme }) => ({
  position: "relative",
  width: 820,
  padding: 15,

  [theme.down("md")]: {
    width: 420,
  },
}));

export const CalendarPrevButton = styled("button")(({ theme }) => ({
  ...theme.mixins.flexCenterCenter,
  position: "absolute",
  left: 15,
  zIndex: 50,
  width: 25,
  height: 25,
  padding: 0,
  backgroundColor: theme.palette.fly.inputBg,
  border: "none",
  borderRadius: 7,
  color: theme.palette.fly.brand,
  cursor: "pointer",
  transition: "all .2s ease",

  "&:disabled": {
    cursor: "default",
    opacity: 0.5,
    pointerEvents: "none",
  },

  "&:hover": {
    backgroundColor: theme.palette.fly.brand,
    color: theme.palette.white,
  },
}));

export const CalendarNextButton = styled(CalendarPrevButton)(({ theme }) => ({
  left: "auto",
  right: 15,
}));

export const CalendarMonths = styled("div")(
  ({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, 50%)",

    [theme.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  }),
  css`
    &.animate-next {
      animation: ${animateNext} 0.3s forwards, ${fadeIn} 0.2s forwards;
    }

    &.animate-prev {
      animation: ${animatePrev} 0.3s forwards, ${fadeIn} 0.2s forwards;
    }
  `
);

export const CalendarMonthBlock = styled("div")(({ theme }) => ({
  ...theme.mixins.flexColumn,
  padding: "0 7.5px",
}));

export const CalendarMonthTitle = styled("span")(({ theme }) => ({
  width: "100%",
  fontSize: 13,
  fontWeight: 600,
  color: "#333",
  lineHeight: "20px",
  textAlign: "center",
  padding: "5px 0 10px",
}));

export const CalendarMonthWeeksBlock = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(7, 50px)",
  gap: 4,
  paddingBottom: 4,
  textTransform: "uppercase",

  [theme.down("xs")]: {
    gridTemplateColumns: "repeat(7, 1fr)",
  },
}));

export const CalendarMonthWeek = styled("span")(({ theme }) => ({
  textAlign: "center",
  color: "#b7b7b7",
  fontSize: 13,
  lineHeight: "20px",
}));

export const CalendarMonthDaysBlock = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(7, 50px)",
  gap: 4,

  [theme.down("xs")]: {
    gridTemplateColumns: "repeat(7, 1fr)",
  },
}));

export const CalendarMonthDay = styled("button")(({ theme, position, type, $selectedIndex }) => ({
  ...theme.mixins.flexCenterCenter,
  flexDirection: "column",
  gap: 4,
  lineHeight: 1,
  width: 50,
  height: 50,
  fontSize: 14,
  backgroundColor: "transparent",
  border: "2px solid transparent",
  borderRadius: 4,

  ...(position
    ? {
        gridColumn: `${position} / ${position + 1}`,
      }
    : {}),

  "&:hover": {
    borderColor: "#2563EB",
    backgroundColor: "rgba(37, 99, 235, 0.1)",
    cursor: "pointer",
  },

  "&.selected": {
    backgroundColor: "#2563EB",
    borderColor: "#2563EB",
    color: "#fff",
  },

  "&.active": {
    backgroundColor: "#2563EB",
    borderColor: "#2563EB",
    color: "#fff",
  },

  "&.disabled": {
    color: "#9e9e9e",
    cursor: "default",
  },

  "&.disabled:hover": {
    borderColor: "transparent",
    backgroundColor: "transparent",
    cursor: "default !important",
  },

  "& > div": {
    fontSize: 10,
    lineHeight: "14px",
    color: "green",
  },

  "&.active > div": {
    color: "#fff",
  },

  [theme.down("xs")]: {
    width: "auto",
    height: 40,
  },
}));
